/**
 * @file useOrderDelivery.ts
 * @description Данные о доставке заказа (phase19.24 шаг 5.2).
 *
 * Один запрос `GET /admin/orders/:id/delivery` на КАРТОЧКУ: хук живёт в
 * `OrderDetailPage`, а вкладки «Доставка» и «Отгрузки» получают его состояние
 * пропом. Способ получения, порядок оплаты, заявка F30, право на правку и
 * перевозчики с расчётом приходят вместе; собирать это из трёх ответов нельзя —
 * экран успевал показать «условия можно менять», пока грузился признак оплаты.
 *
 * Ошибку не глотаем: без ответа сервера блок не знает, заморожены ли условия,
 * и молча открытая форма отправила бы клиенту то, что сервер потом отвергнет.
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError } from "../lib/api-client";
import { getOrderDelivery, type OrderDeliveryDto } from "../lib/api-orders";

export interface OrderDeliveryState {
  data: OrderDeliveryDto | null;
  loading: boolean;
  /** Текст отказа сервера; `null` — всё в порядке. */
  error: string | null;
  /** Перечитать после сохранения исхода, отправки письма или отмены заявки. */
  reload: () => Promise<void>;
}

export function useOrderDelivery(orderId: string | undefined): OrderDeliveryState {
  const [data, setData] = useState<OrderDeliveryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      setData(await getOrderDelivery(orderId));
      setError(null);
    } catch (err) {
      setData(null);
      setError(
        err instanceof ApiError
          ? err.message
          : "Не удалось загрузить доставку заказа",
      );
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, reload: load };
}
